/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import PropTypes from "prop-types"; // Npm: react.js library.
import JoiBrowser from "joi-browser"; // Npm: Joi for frontend validation.
import _ from "underscore"; // Npm: Underscore.js library.
import { toast } from "react-hot-toast"; // Npm: React hot toast.
import { connect } from "react-redux"; // Npm: React Redux for state management.
import { useLazyQuery, useMutation } from "@apollo/client"; // Npm: Apollo client.

import { Flex, useBreakpointValue } from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import SubmitButton from "components/SubmitButton";
import MccMncSelector from "components/MccMncSelector";
import VendorAccountSelector from "components/VendorAccountSearchSelector";
import { MemoizedSelect } from "components/MemoizedInput";

/*
 * GRAPHS
 */
import RoutePlanReadQuery from "./__query__/index.routePlan.read.query";
import RoutePlanUpdateMutation from "./__mutation__/index.routePlan.update.mutation";
import RoutePlanCreateMutation from "./__mutation__/index.routePlan.create.mutation";
import VendorAccountAttachRoutePlanMutation from "./__mutation__/index.vendorAccount.attachRoutePlan.mutation";

/*
 * OBJECTS
 */
const Index = ({ isOpen, isCreateOnly, onClose, passOn, refetch }) => {
  // Hook assignment.
  const [vendorAccount, setVendorAccount] = React.useState(void 0);
  const [
    MutationVendorAccountAttachRoutePlan,
    MutationVendorAccountAttachRoutePlanResponse,
  ] = useMutation(VendorAccountAttachRoutePlanMutation);
  const [error, setError] = React.useState("");
  const [formVersion, _ResetFormInputs] = React.useReducer((j) => j + 1, 0);
  const [, _TriggerRender] = React.useReducer((j) => j + 1, 0);
  const [QueryRoutePlanRead, QueryRoutePlanReadResponse] =
    useLazyQuery(RoutePlanReadQuery);
  const [MutationRoutePlanCreate, MutationRoutePlanCreateResponse] =
    useMutation(RoutePlanCreateMutation);
  const [MutationRoutePlanUpdate, MutationRoutePlanUpdateResponse] =
    useMutation(RoutePlanUpdateMutation);
  const _formDataRef = React.useRef({});
  const _isCurrentViewMobile = useBreakpointValue({
    base: false,
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });
  const _priorityOptions = React.useMemo(() => [1, 2, 3], []);
  const _percentageOptions = React.useMemo(
    () => [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
    [],
  );
  const _smsTypeOptions = React.useMemo(
    () => Object.React.App.enums.SMS_TYPE.enums?.map((i) => i.key),
    [],
  );
  const _shouldShowSmsType = React.useMemo(
    () => ["RESELLER", "RETAIL"].includes(passOn?.type),
    [passOn?.type],
  );

  // Object assignment.
  const _CloseAfterMutation = React.useCallback(async () => {
    // Execute refetch.
    await refetch?.();

    // Close modal.
    return onClose?.();
  }, [onClose, refetch]);

  // Object assignment.
  const _AttachVendorAccountToRoutePlan = React.useCallback(async (__id) => {
    // Only Execute if route has data.
    if (
      !_.isEmpty(vendorAccount) &&
      vendorAccount.includes("(") &&
      vendorAccount.includes(")")
    ) {
      // Const assignment.
      const vendorAccountId = vendorAccount.slice(vendorAccount.lastIndexOf("(") + 1, -1);

      // Execute mutation.
      const _MutationVendorAccountAttachRoutePlan =
        await MutationVendorAccountAttachRoutePlan({
          variables: {
            routePlanId: __id,
            vendorAccountId: vendorAccountId,
          },
        });

      // If mutation caught an exception then report failure.
      if (_MutationVendorAccountAttachRoutePlan instanceof Error)
        return _MutationVendorAccountAttachRoutePlan;

      // Style Guide.
      toast(
        _.every(
          _.pluck(
            _MutationVendorAccountAttachRoutePlan?.data
              ?.VendorAccountAttachRoutePlan,
            "status",
          ),
          (i) => "UPDATE_SUCCESSFUL" === i,
        )
          ? "Successfully attached the vendor with routePlan"
          : "Something went wrong. Please try again after some time.",
      );

      // On successful bind continue submit flow.
      if (
        _.every(
          _.pluck(
            _MutationVendorAccountAttachRoutePlan?.data
              ?.VendorAccountAttachRoutePlan,
            "status",
          ),
          (i) => "UPDATE_SUCCESSFUL" === i,
        )
      ) {
        // Main mutation handler will refetch and close once.
        return void 0;
      }
    }

    // Report void 0.
    return void 0;
  }, [MutationVendorAccountAttachRoutePlan, vendorAccount]);

  // Event handler.
  const _HandleVendorAccountChange = React.useCallback(({ target }) => {
    // Over spreading.
    const { name, value } = target;

    // Update form data.
    _formDataRef.current = {
      ..._formDataRef?.current,
      [name]: value,
    };

    // Update vendorAccount.
    setVendorAccount(value);
  }, []);
  const _HandlePriorityChange = React.useCallback(({ target }) => {
    // Over spreading.
    const { name, value } = target;

    // Update form data.
    _formDataRef.current = {
      ..._formDataRef?.current,
      [name]: isNaN(parseInt(value, 10)) ? 0 : parseInt(value, 10),
    };
  }, []);
  const _HandleSmsTypeChange = React.useCallback(({ target }) => {
    // Over spreading.
    const { name, value } = target;

    // Update form data.
    _formDataRef.current = {
      ..._formDataRef?.current,
      [name]: value,
    };
  }, []);
  const _HandleMccMncChange = React.useCallback(
    (i) => {
      // Update form data.
      _formDataRef.current = {
        ..._formDataRef.current,
        mnc: i.mnc,
        mcc: i.mcc,
      };

      // Refresh selector values without remounting fields.
      _TriggerRender();
    },
    [_TriggerRender],
  );
  const _HandlePercentageChange = React.useCallback(({ target }) => {
    // Over spreading.
    const { name, value } = target;

    // Update form data.
    _formDataRef.current = {
      ..._formDataRef?.current,
      [name]: isNaN(parseFloat(value, 10)) ? 0 : parseFloat(value, 10),
    };
  }, []);

  const _SubmitForm = React.useCallback(async (e) => {
    // Local variable.
    let _routePlanIds;

    // Prevent default behavior.
    e.preventDefault();

    // Reset error.
    setError("");

    // Const assignment.
    const _JoiSchema = JoiBrowser.object({
      vendorAccount: JoiBrowser.string(),
      routeToVendorAccount: JoiBrowser.string(),
      priority: JoiBrowser.number().greater(0).less(4),
      smsType: JoiBrowser.string().optional(),
      mcc: JoiBrowser.string().required(),
      mnc: JoiBrowser.array().items(JoiBrowser.string().required()).required(),
      fakeDlrRate: JoiBrowser.number(),
      fallBackRate: JoiBrowser.number(),
    }).options({ allowUnknown: true });

    // Make sure that mnc is array.
    if (!isCreateOnly && !_.isArray(_formDataRef.current?.mnc))
      _formDataRef.current = {
        ..._formDataRef.current,
        mnc: [_formDataRef.current?.mnc?.split("(")[1]?.split(")")[0]],
      };

    /*
     * Report failure if vendorAccount
     * priority is not set.
     */
    if (_.isEmpty(_formDataRef.current?.vendorAccount))
      return setError("vendorAccount is required.");
    if (
      0 >= _formDataRef.current?.priority ||
      isNaN(_formDataRef.current?.priority)
    )
      return setError("priority is required.");

    // Remove all keys from _formDataRef.current which are undefined.
    _formDataRef.current = {
      ..._.pick(_formDataRef.current, _.identity),
      fakeDlrRate: _formDataRef.current?.fakeDlrRate ?? 0,
      fallBackRate: _formDataRef.current?.fallBackRate ?? 0,
    };

    // Validate form data.
    const _JoiSchemaValidate = _JoiSchema.validate(_formDataRef.current);

    // If error exists then report failure.
    if (_JoiSchemaValidate.error)
      return setError(_JoiSchemaValidate.error?.message);

    /*
     * Update _formDataRef with routePlanId
     * if it doesn't exist and also make sure that mode
     * is not create only.
     */
    if (!isCreateOnly && _.isEmpty(_formDataRef.current?.routePlanId))
      _formDataRef.current = {
        ..._formDataRef.current,
        routePlanId: passOn?.routePlanId,
      };
    if (isCreateOnly && _.isEmpty(_formDataRef.current?.routeId))
      _formDataRef.current = {
        ..._formDataRef.current,
        routeId: passOn?.routeId,
      };

    /*
     * Update _formDataRef with input and routePlan
     * if it doesn't exist. Also make sure that mode is
     * not create only.
     */
    if (_.isEmpty(_formDataRef.current?.input))
      _formDataRef.current = {
        input: [_formDataRef.current],
        ..._formDataRef.current,
      };

    // Execute update mutation.
    const _MutationRoutePlanUpdate = await [
      isCreateOnly ? MutationRoutePlanCreate : MutationRoutePlanUpdate,
    ]?.[0]({
      variables: {
        routePlanId: _.isEmpty(passOn?.routePlanIds)
          ? isCreateOnly
            ? ""
            : passOn?.routePlanId
          : void 0,
        routePlanIds: _.isEmpty(passOn?.routePlanIds)
          ? void 0
          : isCreateOnly
            ? ""
            : passOn?.routePlanIds,
        input: _.pick(_formDataRef.current, "input").input.map((j) => {
          // Const assignment.
          let _mcc, _mnc;

          // Variable assignment.
          _mcc = j?.mcc;
          _mnc = j?.mnc;

          // Conditionally update mcc and mnc.
          if (j?.mcc && (j?.mcc?.includes("(") || j?.mcc?.includes(")")))
            _mcc = j.mcc.split("(")[1].split(")")[0];
          if (j?.mnc && 0 < j?.mnc?.length)
            _mnc = _.compact(
              _.flatten(
                j?.mnc.map((r) =>
                  r?.includes("(") && r?.includes(")")
                    ? r.split("(")[1].split(")")[0]
                    : r,
                ),
              ),
            );

          // Return data.
          return {
            fakeDlrRate: j?.fakeDlrRate,
            fallBackRate: j?.fallBackRate,
            mcc: _mcc,
            mnc: _mnc,
            priority: j?.priority,
            smsType: j?.smsType,
            ...(isCreateOnly ? { routeId: passOn?.routeId } : {}),
          };
        }),
      },
    });

    // If mutation caught an exception then report failure.
    if (_MutationRoutePlanUpdate instanceof Error)
      return _MutationRoutePlanUpdate;

    // If Update contains data then update routePlanIds.
    if (!_.isEmpty(_MutationRoutePlanUpdate?.data?.RoutePlanUpdate))
      _routePlanIds = _.compact(
        _.pluck(_MutationRoutePlanUpdate?.data?.RoutePlanUpdate, "id"),
      );
    if (!_.isEmpty(_MutationRoutePlanUpdate?.data?.RoutePlanCreate))
      _routePlanIds = _.compact(
        _.pluck(_MutationRoutePlanUpdate?.data?.RoutePlanCreate, "id"),
      );

    // Attach vendor account and route to customer.
    !_.isEmpty(_routePlanIds) &&
      _.isArray(_routePlanIds) &&
      (await _AttachVendorAccountToRoutePlan(_routePlanIds));

    // Check if create was successful.
    if (
      !_.isEmpty(_MutationRoutePlanUpdate?.data?.RoutePlanCreate) &&
      _.every(
        _.pluck(_MutationRoutePlanUpdate?.data?.RoutePlanCreate, "status") ??
          [],
        (j) => "CREATE_SUCCESSFUL" === j,
      )
    ) {
      // Style guide.
      toast("Successfully upserted the route plan.");

      // Close after mutation.
      return _CloseAfterMutation();
    }
    if (
      !_.isEmpty(_MutationRoutePlanUpdate?.data?.RoutePlanUpdate) &&
      _.every(
        _.pluck(_MutationRoutePlanUpdate?.data?.RoutePlanUpdate, "status") ??
          [],
        (j) => "UPDATE_SUCCESSFUL" === j,
      )
    ) {
      // Style guide.
      toast("Successfully upserted the route plan.");

      // Close after mutation.
      return _CloseAfterMutation();
    }

    // Style Guide.
    toast(
      _.isEmpty(_MutationRoutePlanUpdate?.data?.RoutePlanCreate)
        ? [
            0 ===
            _.pluck(_MutationRoutePlanUpdate?.data?.RoutePlanUpdate, "message")
              ?.length
              ? "Same plan already exists. kindly delete it or modify it instead."
              : _.pluck(
                  _MutationRoutePlanUpdate?.data?.RoutePlanUpdate,
                  "message",
                ),
          ]?.join(" ")
        : _.pluck(
            _MutationRoutePlanUpdate?.data?.RoutePlanCreate,
            "message",
          )?.join(" "),
    );

    // Report void.
    return void 0;
  }, [
    _AttachVendorAccountToRoutePlan,
    _CloseAfterMutation,
    MutationRoutePlanCreate,
    MutationRoutePlanUpdate,
    isCreateOnly,
    passOn?.routeId,
    passOn?.routePlanId,
    passOn?.routePlanIds,
  ]);

  // Event handler.
  React.useEffect(() => {
    // Do not call read query while modal is closed.
    if (false === isOpen) return void 0;

    // Reset create form without hitting route plan read query.
    if (isCreateOnly) {
      // Reset form data.
      _formDataRef.current = {};

      // Reset selected vendor account.
      setVendorAccount(void 0);

      // Reset form inputs.
      _ResetFormInputs();

      // Report void 0.
      return void 0;
    }

    // Do not call read query without route plan id.
    if (_.isEmpty(passOn?.routePlanId)) return void 0;

    // Local variable.
    let _isActive = true;

    // Reset previous form data while new route plan loads.
    _formDataRef.current = {};

    // Reset selected vendor account.
    setVendorAccount(void 0);

    // _Async handler.
    const _Async = async () => {
      // Const assignment.
      const _QueryRoutePlanReadQuery = await QueryRoutePlanRead({
        variables: {
          routePlanId: passOn?.routePlanId,
        },
      });

      // If query caught an exception then report failure.
      if (_QueryRoutePlanReadQuery instanceof Error)
        return _QueryRoutePlanReadQuery;

      // Avoid updating state after component closes/unmounts.
      if (!_isActive) return void 0;

      /*
       * If routePlan details fetch complete then
       * update its value.
       */
      if (_QueryRoutePlanReadQuery?.data?.RoutePlanRead) {
        // Const assignment.
        const _routePlanRead = _.first(
          _QueryRoutePlanReadQuery?.data?.RoutePlanRead,
        );

        // Update form data.
        _formDataRef.current = {
          routePlanId: passOn?.routePlanId,
          createdAt: _routePlanRead?.createdAt,
          updatedAt: _routePlanRead?.updatedAt,
          mcc: _routePlanRead?.Mcc?.mcc
            ? `${_routePlanRead?.Mcc?.countryName} (${_routePlanRead?.Mcc?.mcc})`
            : void 0,
          mnc: _routePlanRead?.Mnc?.mnc
            ? `(${_routePlanRead?.Mnc?.mnc}) ${_routePlanRead?.Mnc?.network}`
            : void 0,
          countryName: _routePlanRead?.Mcc?.countryName,
          priority: _routePlanRead?.priority,
          isPaused: _routePlanRead?.isPaused,
          smsType: _routePlanRead?.smsType,
          fakeDlrRate: _routePlanRead?.fakeDlrRate,
          fallBackRate: _routePlanRead?.fallBackRate,
          vendorAccount: _routePlanRead?.VendorAccount?.displayName
            ? `${_routePlanRead?.VendorAccount?.displayName} (${_routePlanRead?.VendorAccount?.id})`
            : void 0,
        };

        // Reset form inputs after route plan read.
        return _ResetFormInputs();
      }

      // Report failure.
      return void 0;
    };
    _Async();

    // Cleanup active flag.
    return () => {
      _isActive = false;
    };
  }, [
    QueryRoutePlanRead,
    _ResetFormInputs,
    isCreateOnly,
    isOpen,
    passOn?.routePlanId,
  ]);

  // Const assignment.
  const _isLoading =
    MutationVendorAccountAttachRoutePlanResponse.loading ||
    MutationRoutePlanUpdateResponse.loading ||
    MutationRoutePlanCreateResponse.loading;
  const _isInputDisabled = isCreateOnly
    ? false
    : _isLoading || QueryRoutePlanReadResponse.loading;

  // Return component.
  return (
    <form onSubmit={_SubmitForm} className="routePlanUpsert">
      <Flex gap="22px" flexDir="column" wrap="nowrap" w="100%">
        <Flex width="100%" gap="18px" direction={{ base: "column", md: "row" }}>
          <VendorAccountSelector
            disabled={_isInputDisabled}
            name="vendorAccount"
            label="Vendor Account"
            placeholder='e.g. "Awesome Vendor Account"'
            value={_formDataRef?.current?.vendorAccount}
            isRequired={true}
            error={error}
            height="40px"
            isInvalid={
              error?.includes("vendorAccount") && !error?.includes("position")
            }
            onChange={_HandleVendorAccountChange}
          />
          <MemoizedSelect
            disabled={_isInputDisabled}
            name="priority"
            label="Priority"
            placeholder="Enter Priority"
            onChange={_HandlePriorityChange}
            error={error}
            isInvalid={error?.includes("priority")}
            value={_formDataRef?.current?.priority}
            options={_priorityOptions}
            isRequired
          />
          {_shouldShowSmsType ? (
            <MemoizedSelect
              disabled={_isInputDisabled}
              name="smsType"
              label="SMS Type"
              placeholder="Enter SMS Type"
              onChange={_HandleSmsTypeChange}
              error={error}
              isInvalid={error?.includes("smsType")}
              data={_formDataRef?.current?.smsType}
              options={_smsTypeOptions}
            />
          ) : (
            void 0
          )}
        </Flex>
        <Flex width="100%" gap="22px" direction={{ base: "column", md: "row" }}>
          <MccMncSelector
            isRequired={true}
            disabled={_isInputDisabled}
            mccValue={_formDataRef?.current?.mcc}
            mncValue={_formDataRef?.current?.mnc}
            inValidMnc={error?.includes("mnc")}
            inValidMcc={error?.includes("mcc")}
            onChange={_HandleMccMncChange}
          />
        </Flex>
        <Flex
          width="100%"
          gap="22px"
          direction={{ base: "column", md: "row" }}
          zIndex={0}
          key={formVersion}
        >
          <MemoizedSelect
            disabled={_isInputDisabled}
            name="fakeDlrRate"
            label="Cutting Rate (in Percentage)"
            placeholder='e.g. "10 is 10%"'
            onChange={_HandlePercentageChange}
            error={error}
            isInvalid={error?.includes("fakeDlrRate")}
            data={_formDataRef?.current?.fakeDlrRate}
            options={_percentageOptions}
          />
          <MemoizedSelect
            disabled={_isInputDisabled}
            name="fallBackRate"
            label="Fallback Rate"
            placeholder='e.g. "10 is 10%"'
            onChange={_HandlePercentageChange}
            error={error}
            isInvalid={error?.includes("fallBackRate")}
            data={_formDataRef?.current?.fallBackRate}
            options={_percentageOptions}
          />
        </Flex>
      </Flex>
      <Flex
        mt="22px"
        alignItems={_isCurrentViewMobile ? "flex-end" : "center"}
        justifyContent="space-between"
        flexDir="row"
      >
        <SubmitButton
          mt={0}
          disabled={_isInputDisabled}
          onSubmit={_SubmitForm}
          defaultText={isCreateOnly ? "Create RoutePlan" : "Update RoutePlan"}
          isLoading={_isLoading}
        />
      </Flex>
    </form>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {
  isCreateOnly: PropTypes.bool,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  passOn: PropTypes.object,
  submitRef: PropTypes.object,
  disabled: PropTypes.bool,
  isUpsertMixerContext: PropTypes.bool,
  setSubmitIsLoading: PropTypes.func,
  refetch: PropTypes.func,
};

/*
 * REDUX
 */
const _MapStateToProps = (__state) => ({ passOn: __state.PassOn });

/*
 * EXPORT
 */
export default connect(_MapStateToProps)(React.memo(Index));
